import React from 'react';

// Rebuilt (not scanned) SVS quarterly notice used as an illustration on the
// SVS knowledge-base guide. The document itself is always German — it depicts
// a real German-language government letter — on both the English and German
// version of the guide; only the legend underneath is translated. See §12 of
// the content brief: this component must not be translated, and the MOCK bar
// + watermark must never be removed while this is a placeholder rebuild
// rather than a real anonymized scan.
interface SvsVorschreibungMockProps {
  legendTitle: string;
  legend: [React.ReactNode, React.ReactNode, React.ReactNode, React.ReactNode];
}

const MarkerBadge: React.FC<{ n: number; className?: string }> = ({ n, className = '' }) => (
  <span
    aria-hidden="true"
    className={`inline-flex ml-2 align-middle w-5 h-5 shrink-0 rounded-full bg-red-600 text-white text-[11px] font-bold items-center justify-center ${className}`}
  >
    {n}
  </span>
);

export const SvsVorschreibungMock: React.FC<SvsVorschreibungMockProps> = ({ legendTitle, legend }) => {
  return (
    <figure className="my-10">
      <div className="relative bg-white rounded-xl shadow-md border border-pstax-border overflow-hidden p-6 sm:p-8" lang="de">
        <div className="absolute top-0 left-0 bg-red-600 text-white text-[10px] sm:text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-br-lg z-20">
          MOCK — NOT A REAL DOCUMENT
        </div>

        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center z-10 select-none overflow-hidden"
          style={{ opacity: 0.17 }}
        >
          <span className="text-red-600 font-black text-[80px] sm:text-[130px] -rotate-[28deg] tracking-widest whitespace-nowrap">
            MOCK
          </span>
        </div>

        <div className="relative z-0 pt-8">
          <div className="flex flex-wrap justify-between items-start gap-4 border-b border-pstax-border pb-4 mb-6">
            <div>
              <p className="font-bold text-pstax-navy leading-tight">Sozialversicherungsanstalt der Selbständigen</p>
              <p className="text-sm text-pstax-gray">Landesstelle Wien · Beitragsabteilung</p>
            </div>
            <div className="text-right text-sm text-pstax-gray">
              <p>Versicherungsnummer</p>
              <p className="font-mono font-bold text-pstax-navy">0000 01 01 80</p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg sm:text-xl font-display font-bold text-pstax-navy inline-flex items-center flex-wrap">
              Vorschreibung 1. Quartal 2026
              <MarkerBadge n={1} />
            </h3>
            <p className="text-sm text-pstax-gray mt-1">Beitragszeitraum 01.01.2026 – 31.03.2026 · Fälligkeit: 28.02.2026</p>
          </div>

          <div className="bg-pstax-bg border border-pstax-border rounded-lg px-4 py-3 mb-6 font-semibold text-pstax-navy inline-flex items-center flex-wrap w-full">
            <span>Vorläufige monatliche Beitragsgrundlage — EUR 551,10</span>
            <MarkerBadge n={2} />
          </div>

          <dl className="space-y-2 text-sm border-t border-pstax-border pt-4">
            <div className="flex justify-between gap-4">
              <dt className="text-pstax-gray">Pensionsversicherung (18,50 %)</dt>
              <dd className="font-medium">305,85</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-pstax-gray">Krankenversicherung (6,80 %)</dt>
              <dd className="font-medium">112,44</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-pstax-gray">Selbständigenvorsorge (1,53 %)</dt>
              <dd className="font-medium">25,29</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-pstax-gray">Unfallversicherung (Fixbetrag)</dt>
              <dd className="font-medium">38,85</dd>
            </div>
            <div className="flex justify-between items-center gap-4 font-bold text-pstax-navy border-t border-pstax-border pt-2 mt-2">
              <dt className="inline-flex items-center">
                Zu zahlender Betrag
                <MarkerBadge n={3} />
              </dt>
              <dd>EUR 482,43</dd>
            </div>
          </dl>

          <p className="text-xs text-pstax-gray mt-6 pt-4 border-t border-pstax-border leading-relaxed">
            Die Beiträge werden vorläufig vorgeschrieben und nach Vorliegen des rechtskräftigen Einkommensteuerbescheides endgültig festgestellt (§ 25 GSVG).
            <MarkerBadge n={4} />
          </p>
        </div>
      </div>

      <div className="mt-4 bg-white border border-pstax-border rounded-xl p-5">
        <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">{legendTitle}</p>
        <ol className="space-y-2 text-sm text-pstax-navy">
          {legend.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span
                aria-hidden="true"
                className="shrink-0 w-5 h-5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center mt-0.5"
              >
                {i + 1}
              </span>
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ol>
      </div>
    </figure>
  );
};
